import React, { useEffect, useState } from 'react';
import { Route, BrowserRouter as Router } from "react-router-dom";
import { useIdleTimer } from 'react-idle-timer'
import {ThemeProvider} from '@mui/material';

import ModuleWrapper from "../ModuleWrapper";
import Keys from '../../pages/Keys';
import Licenses from '../../pages/Licenses';
import Logo from './Logo';

import styles from './App.module.css';
import Settings from '../../pages/Settings';
import {renderNavigation, renderRouting} from '../../utils/routing';
import SessionInfo, {Permit, Role} from '../SessionInfo';
import {resetSessionTime, setSessionTime} from '../../utils/sessionTime';
import Signature from '../../pages/Signature';
import AppLicenseModal from '../AppLicenseModal';
import {theme} from '../../theme';
import Sessions from "../../pages/Sessions";
import {getSessionInfo, resetSessionInfo, storeSessionInfo} from "../../utils/sessionInfo";
import {hetch} from "../../utils/fetch.hooked";
import Audit from "../../pages/Audit";
import brandingService from '../../services/BrandingService';

//eslint-disable-next-line no-undef
const clientVersion = VERSION ?? '-';

const oneMinute = 60000;
const defaultLogoutTimeout = oneMinute;

function App() {

    const [appName, setAppName] = useState(brandingService.getAppName());
    const [companyName, setCompanyName] = useState(brandingService.getCompanyName());
    const [showLogo, setShowLogo] = useState(brandingService.shouldShowLogo());
    const hasAppName = Boolean(String(appName ?? '').trim());

    const navStyle = {
        width: `calc(100% - ${showLogo ? 78 : 0}px - ${hasAppName ? 122 : 0}px)`,
        minWidth: 0,
    };

    useEffect(() => {
        brandingService.init().then(() => {
            setAppName(brandingService.getAppName());
            setCompanyName(brandingService.getCompanyName());
            setShowLogo(brandingService.shouldShowLogo());
            document.title = brandingService.getDocumentTitle()
        });
    }, []);

    const [sessionInfo, setSessionInfo] = React.useState(getSessionInfo());
    const routes = {}
    if (sessionInfo.signatoreUi ) {
        routes['/signature'] =
        {
            label: 'Наложение УКЭП',
            renderContent: () => <Signature permission={ Permit.write(Role.SIGNATORY).read(Role.ADMIN_INFORMATION_SECURITY).get(sessionInfo.role) } />,
            isDefault: sessionInfo.role !== Role.ADMIN,
            hasContentBorder: false,
            displayFor: [Role.SIGNATORY, Role.ADMIN_INFORMATION_SECURITY]
        }
    }

    routes['/certificates'] = {
            label: 'Управление сертификатами',
            renderContent: () => <Keys permission={ Permit.write(Role.ADMIN).read(Role.ADMIN_INFORMATION_SECURITY).get(sessionInfo.role) } />,
            isDefault: sessionInfo.role === Role.ADMIN,
            hasContentBorder: true,
            displayFor: Role.ADMINS
        }
    routes['/licenses'] = {
            label: 'Управление лицензией',
            renderContent: () => <Licenses permission={ Permit.write(Role.ADMIN).read(Role.ADMIN_INFORMATION_SECURITY).get(sessionInfo.role) } />,
            isDefault: false,
            hasContentBorder: true,
            displayFor: Role.ADMINS
        }
    routes['/settings'] = {
            label: 'Настройки',
            renderContent: () => <Settings sessionInfo={sessionInfo} />,
            isDefault: false,
            hasContentBorder: true,
            displayFor: Role.ADMINS
        }
    routes['/sessions'] = {
            label: 'Журнал сеансов',
            renderContent: () => <Sessions permission={ Permit.write(Role.ADMIN_INFORMATION_SECURITY).read(Role.ADMIN).get(sessionInfo.role) }/>,
            isDefault: false,
            hasContentBorder: true,
            displayFor: Role.ADMINS
        }
    routes['/audit'] = {
            label: 'Журнал событий',
            renderContent: () => <Audit permission={ Permit.write(Role.ADMIN_INFORMATION_SECURITY, Role.ADMIN).get(sessionInfo.role) }/>,
            isDefault: false,
            hasContentBorder: true,
            displayFor: Role.ADMINS
        }
    const [logoutTimeout, setLogoutTimeout] = useState(defaultLogoutTimeout);
    const [serverVersion, setServerVersion] = useState('-');
    useEffect(() => {
        hetch('/api/currentSession')
            .then(res => res.json())
            .then((r) => {
                storeSessionInfo(r);
                if(r.hasOwnProperty('sessionStart')) {
                    setSessionTime(r.sessionStart)
                }
                setSessionInfo(r);
            })
            .catch(console.error)
    }, []);

    const logout = async () => {
        try {
            const logoutResponse = await hetch('/api/logoutCurrentUser', {
                headers: {'Content-Type': 'application/json'},
                method: 'POST',
                body: null
            });

            const logoutUrl = (await logoutResponse.text()).trim();

            resetSessionTime();
            resetSessionInfo();

            if (logoutUrl) {
                window.location.replace(logoutUrl);
            } else {
                window.location.reload();
            }

        } catch (err) {
            console.error('Ошибка выхода');
        }
    }
  useEffect(() => {
      hetch('/api/config/')
          .then(res => res.json())
          .then((data) => {
              const timeout = data['token-timeout-min'];
              if (typeof timeout === 'number') {
                  setLogoutTimeout(timeout * oneMinute);
              }
          })
          .catch(console.error);
  }, []);

  useIdleTimer({ onIdle: logout, timeout: logoutTimeout });

  useEffect(() => {
      hetch('/api/health-checker/about')
          .then(res => res.json())
          .then((data) => {
              setServerVersion(data.appVersion ?? '-')
          })
          .catch(console.error)
  }, []);

  return (
      sessionInfo?.role ?
    <ThemeProvider theme={theme}>
      <div className={styles.app}>
        <div className={styles.appBody}>
          <Router>
            <Route
              path="/"
              render={() => (
                <div className={styles.appHeader}>
                  <div className={styles.filler}/>
                  <div className={styles.headerContainer}>
                  { showLogo && (
                      <div className={styles.logo}>
                          <Logo/>
                      </div>
                  )}

                    <div className={styles.navigation} style={navStyle}>
                      {renderNavigation({routes, styles, sessionInfo})}
                    </div>

                    {hasAppName && (
                        <div className={styles.nameContainer}>
                            <span className={styles.name}>{appName}</span>
                        </div>
                    )}

                  </div>
                </div>
              )}
            />
            {renderRouting({
              routes,
              wrapContent: ({route, content}) => (
                  route.displayFor.includes(sessionInfo.role)
                      ? <ModuleWrapper hasContentBorder={route.hasContentBorder} menu={<SessionInfo sessionInfo={sessionInfo}/>} logout={logout}>
                          {content}
                        </ModuleWrapper>
                      : <div className={styles.accessDenied}><span>Страница не доступна к просмотру</span></div>
              ),
            })}
          </Router>
        </div>
        <div className={styles.appFooter}>
          <div>
            <div>
              {`Версия сервера: ${serverVersion}, версия клиента: ${clientVersion} © 2023-${new Date().getFullYear()} ${companyName}`}
            </div>
          </div>
        </div>
        <AppLicenseModal/>
      </div>
    </ThemeProvider>
          : null
  );
}

export default App;
